import { X, Minus, ChevronUp, Loader2, Check, AlertTriangle, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useBatchUpload } from '../contexts/BatchUploadContext';

export function BatchUploadPanel() {
  const { state, dismiss, toggleMinimize, retryFile } = useBatchUpload();
  const navigate = useNavigate();

  if (!state.isActive) return null;

  const isContracts = state.uploadType === 'contracts';
  const entityLabel = isContracts ? 'contracts' : 'quotes';
  const doneCount = state.files.filter((f) => f.status === 'done').length;
  const errorCount = state.files.filter((f) => f.status === 'error').length;
  const duplicateCount = state.files.filter((f) => f.status === 'duplicate').length;
  const settledCount = doneCount + errorCount + duplicateCount;
  const allDone = settledCount === state.files.length;
  const progress = (settledCount / state.files.length) * 100;

  // Collapsed state
  if (state.isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {!allDone && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            {allDone && <Check className="h-4 w-4 text-green-500" />}
            <span className="text-sm font-medium">
              {allDone ? `${doneCount} ${entityLabel} added` : `Analyzing ${doneCount + 1} of ${state.files.length}`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggleMinimize} className="rounded p-1 hover:bg-muted">
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            </button>
            <button onClick={dismiss} className="rounded p-1 hover:bg-muted">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
        {!allDone && (
          <div className="h-1 bg-muted">
            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    );
  }

  // Expanded state
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 ${allDone && doneCount > 0 ? 'bg-green-500/10' : 'bg-muted/50'}`}>
        <div className="flex items-center gap-2">
          {allDone && doneCount > 0 && <Check className="h-4 w-4 text-green-500" />}
          <span className="text-sm font-semibold">
            {allDone ? `${doneCount} ${entityLabel} ready` : `Analyzing ${doneCount + 1} of ${state.files.length}`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleMinimize} className="rounded p-1 hover:bg-muted">
            <Minus className="h-4 w-4 text-muted-foreground" />
          </button>
          <button onClick={dismiss} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {!allDone && (
        <div className="h-1 bg-muted">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* File list */}
      <div className="max-h-80 overflow-y-auto">
        {state.files.map((file, i) => {
          const targetItemId =
            file.status === 'done'
              ? file.itemId
              : file.status === 'duplicate'
                ? file.duplicateOfItemId
                : undefined;
          const isClickable = !!targetItemId && !!state.projectId;

          return (
            <div key={i} className="border-b border-border last:border-0">
              <div
                className={`flex items-center gap-3 px-4 py-2.5 ${file.status === 'processing' ? 'bg-primary/5' : ''} ${isClickable ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                onClick={isClickable ? () => navigate(`/projects/${state.projectId}?item=${targetItemId}`) : undefined}
              >
                {file.status === 'done' && <Check className="h-4 w-4 shrink-0 text-green-500" />}
                {file.status === 'processing' && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />}
                {file.status === 'queued' && <div className="h-4 w-4 shrink-0" />}
                {file.status === 'duplicate' && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />}
                {file.status === 'error' && <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{file.vendorName || file.fileName}</div>
                  {file.status === 'duplicate' && file.duplicateOfVendorName ? (
                    <div className="truncate text-xs text-amber-600">
                      Already uploaded for {file.duplicateOfVendorName}
                    </div>
                  ) : (
                    file.vendorName && <div className="truncate text-xs text-muted-foreground">{file.fileName}</div>
                  )}
                </div>
                {file.status === 'done' && (
                  <span className="shrink-0 text-xs text-green-500">Saved</span>
                )}
                {file.status === 'processing' && (
                  <span className="shrink-0 text-xs text-primary">Analyzing...</span>
                )}
                {file.status === 'queued' && (
                  <span className="shrink-0 text-xs text-muted-foreground">Queued</span>
                )}
                {file.status === 'duplicate' && (
                  <span className="shrink-0 text-xs text-amber-600">Duplicate</span>
                )}
                {file.status === 'error' && (
                  <button onClick={(e) => { e.stopPropagation(); retryFile(i); }} className="shrink-0 rounded p-1 hover:bg-muted">
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
